import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, Unlink, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { StatusChip } from "@/components/common/StatusChip";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { SyncCustomersDialog } from "@/features/settings/SyncCustomersDialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BRANDS = ["bb", "betos"];
const BRAND_LABEL = { bb: "B&B", betos: "Betos" };

const CONN_TONE = { conectado: "green", error: "red", sin_conectar: "gray" };

const ConnectionCard = ({ brand, conn, provider, onConnect, onDisconnect, onSync, onToggleCustomNumbers, busy }) => {
  const { t } = useT();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const estado = conn?.estado || "sin_conectar";

  return (
    <div data-testid={`qbo-connection-${brand}`} className="border border-line p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-tertiary">
            {BRAND_LABEL[brand]}
          </p>
          <p className="mt-1 text-[15px] font-semibold text-ink">
            {conn?.company_name || t("settings.accounting.not_connected")}
          </p>
        </div>
        <StatusChip
          testId={`qbo-connection-${brand}-status`}
          tone={CONN_TONE[estado] || "gray"}
          label={t(`settings.accounting.states.${estado}`)}
        />
      </div>

      {estado === "conectado" && (
        <p className="mt-2 text-[12.5px] text-ink-secondary">
          {t("settings.accounting.connected_since", { date: formatDateTime(conn.connected_at) })}
          {conn.connected_by_nombre ? ` · ${conn.connected_by_nombre}` : ""}
        </p>
      )}
      {estado === "conectado" && conn.last_refresh_at && (
        <p className="mt-0.5 text-[11.5px] text-ink-tertiary" data-testid={`qbo-last-refresh-${brand}`}>
          {t("settings.accounting.last_refresh", { date: formatDateTime(conn.last_refresh_at) })}
        </p>
      )}
      {estado === "error" && conn?.error && (
        <p className="mt-2 text-[12.5px] text-state-critical" data-testid={`qbo-connection-${brand}-error`}>
          {conn.error}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        {estado === "conectado" ? (
          <>
            <Button
              data-testid={`qbo-sync-customers-${brand}`}
              variant="outline" size="sm" disabled={busy}
              onClick={() => onSync(brand)}
              className="h-8 rounded-control text-[12.5px]"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              {t("settings.accounting.sync_customers")}
            </Button>
            <Button
              data-testid={`qbo-disconnect-${brand}`}
              variant="outline" size="sm" disabled={busy}
              onClick={() => setConfirmOpen(true)}
              className="h-8 rounded-control text-[12.5px] text-state-critical hover:bg-state-critical-soft"
            >
              <Unlink className="mr-1.5 h-3.5 w-3.5" />
              {t("settings.accounting.disconnect")}
            </Button>
          </>
        ) : (
          <Button
            data-testid={`qbo-connect-${brand}`}
            size="sm" disabled={busy}
            onClick={() => onConnect(brand)}
            className="h-8 rounded-control bg-brand text-[12.5px] text-paper hover:bg-brand-hover"
          >
            <Link2 className="mr-1.5 h-3.5 w-3.5" />
            {t("settings.accounting.connect")}
          </Button>
        )}
      </div>

      {estado === "conectado" && provider === "simulator" && (
        <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
          <div>
            <p className="text-[12px] text-ink">{t("settings.accounting.custom_txn_numbers")}</p>
            <p className="text-[11px] text-ink-tertiary">{t("settings.accounting.custom_txn_numbers_hint")}</p>
          </div>
          <Switch
            data-testid={`qbo-custom-txn-numbers-${brand}`}
            checked={conn.custom_txn_numbers !== false}
            onCheckedChange={(checked) => onToggleCustomNumbers(brand, checked)}
            disabled={busy}
          />
        </div>
      )}

      <ConfirmDialog
        testId={`qbo-disconnect-confirm-${brand}`}
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("settings.accounting.disconnect")}
        description={t("settings.accounting.disconnect_confirm", { brand: BRAND_LABEL[brand] })}
        destructive
        onConfirm={() => { setConfirmOpen(false); onDisconnect(brand); }}
      />
    </div>
  );
};

export const AccountingTab = () => {
  const { t } = useT();
  const qc = useQueryClient();

  const { data: connections } = useQuery({
    queryKey: ["qbo-connections"],
    queryFn: async () => (await api.get("/qbo/connections")).data,
  });
  const { data: qboSettings } = useQuery({
    queryKey: ["qbo-settings"],
    queryFn: async () => (await api.get("/qbo/settings")).data,
  });
  const { data: serviceTypes } = useQuery({
    queryKey: ["service-types"],
    queryFn: async () => (await api.get("/service-types")).data,
  });

  const connectMut = useMutation({
    mutationFn: (brand) => api.post(`/qbo/connections/${brand}/connect`),
    onSuccess: ({ data }) => {
      if (data?.redirect_url) { window.location.href = data.redirect_url; return; }
      qc.invalidateQueries({ queryKey: ["qbo-connections"] });
      toast.success(t("settings.accounting.connected"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const disconnectMut = useMutation({
    mutationFn: (brand) => api.post(`/qbo/connections/${brand}/disconnect`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qbo-connections"] });
      toast.success(t("settings.accounting.disconnected"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const toggleCustomNumbersMut = useMutation({
    mutationFn: ({ brand, checked }) => api.patch(`/qbo/simulator/${brand}/settings`, { custom_txn_numbers: checked }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qbo-connections"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const saveRule = useMutation({
    mutationFn: (payload) => api.patch("/qbo/settings", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qbo-settings"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const cfg = qboSettings?.facturacion_qbo || {};
  const busy = connectMut.isPending || disconnectMut.isPending || toggleCustomNumbersMut.isPending;
  const [syncBrand, setSyncBrand] = useState(null);

  const byBrand = Object.fromEntries((connections || []).map((c) => [c.brand, c]));

  const setMapaServicio = (serviceId, brand) => {
    const mapa = { ...(cfg.mapa_servicio_brand || {}) };
    if (brand === "none") delete mapa[serviceId];
    else mapa[serviceId] = brand;
    saveRule.mutate({ mapa_servicio_brand: mapa });
  };

  return (
    <div data-testid="qbo-accounting-tab" className="space-y-6">
      <p className="text-[12.5px] text-ink-tertiary">
        {t("settings.accounting.provider_note", { provider: qboSettings?.provider === "intuit" ? "QuickBooks (real)" : "Simulador" })}
      </p>

      <div>
        <p className="mb-2.5 text-[13px] font-semibold text-ink">{t("settings.accounting.connections_title")}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {BRANDS.map((brand) => (
            <ConnectionCard
              key={brand} brand={brand} conn={byBrand[brand]} provider={qboSettings?.provider} busy={busy}
              onConnect={(b) => connectMut.mutate(b)}
              onDisconnect={(b) => disconnectMut.mutate(b)}
              onSync={(b) => setSyncBrand(b)}
              onToggleCustomNumbers={(b, checked) => toggleCustomNumbersMut.mutate({ brand: b, checked })}
            />
          ))}
        </div>
      </div>

      <SyncCustomersDialog brand={syncBrand} open={!!syncBrand} onOpenChange={(v) => !v && setSyncBrand(null)} />

      <div className="border-t border-line pt-5">
        <p className="mb-2.5 text-[13px] font-semibold text-ink">{t("settings.accounting.rule_title")}</p>
        <p className="mb-3 max-w-xl text-[12.5px] text-ink-secondary">{t("settings.accounting.rule_desc")}</p>
        <div className="grid gap-3.5 sm:grid-cols-2 sm:max-w-lg">
          <div>
            <p className="mb-1.5 text-[12px] text-ink-tertiary">{t("settings.accounting.rule_label")}</p>
            <Select value={cfg.regla || "por_edificio"} onValueChange={(v) => saveRule.mutate({ regla: v })}>
              <SelectTrigger data-testid="qbo-rule-select" className="h-9 rounded-control text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["por_edificio", "por_compania", "por_tipo_servicio", "manual"].map((r) => (
                  <SelectItem key={r} value={r}>{t(`settings.accounting.rules.${r}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="mb-1.5 text-[12px] text-ink-tertiary">{t("settings.accounting.trigger_label")}</p>
            <Select value={cfg.trigger_status || "cerrada"} onValueChange={(v) => saveRule.mutate({ trigger_status: v })}>
              <SelectTrigger data-testid="qbo-trigger-select" className="h-9 rounded-control text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aprobada">{t("woStatus.aprobada")}</SelectItem>
                <SelectItem value="cerrada">{t("woStatus.cerrada")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {cfg.regla === "por_tipo_servicio" && (
          <div className="mt-4" data-testid="qbo-service-map">
            <p className="mb-2 text-[12px] text-ink-tertiary">{t("settings.accounting.service_map_title")}</p>
            <table className="w-full max-w-lg text-[13px]">
              <tbody>
                {(serviceTypes?.items || []).map((s) => (
                  <tr key={s.id} className="border-b border-line" data-testid={`qbo-service-map-row-${s.id}`}>
                    <td className="py-1.5 pr-3 text-ink">{s.nombre}</td>
                    <td className="w-40 py-1.5">
                      <Select
                        value={(cfg.mapa_servicio_brand || {})[s.id] || "none"}
                        onValueChange={(v) => setMapaServicio(s.id, v)}
                      >
                        <SelectTrigger data-testid={`qbo-service-map-select-${s.id}`} className="h-8 rounded-control text-[12.5px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t("settings.accounting.use_building_brand")}</SelectItem>
                          <SelectItem value="bb">B&amp;B</SelectItem>
                          <SelectItem value="betos">Betos</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Fix M (2026-09-28): cómo se arma cada orden en QuickBooks. */}
        <div className="mt-5 space-y-3 sm:max-w-lg" data-testid="qbo-project-options">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[13px] text-ink">{t("settings.accounting.project_per_order")}</p>
              <p className="text-[11.5px] text-ink-tertiary">{t("settings.accounting.project_per_order_hint")}</p>
            </div>
            <Switch data-testid="qbo-project-per-order" checked={cfg.proyecto_por_orden !== false}
              onCheckedChange={(v) => saveRule.mutate({ proyecto_por_orden: v })} />
          </div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[13px] text-ink">{t("settings.accounting.sub_cost_to_project")}</p>
              <p className="text-[11.5px] text-ink-tertiary">{t("settings.accounting.sub_cost_to_project_hint")}</p>
            </div>
            <Switch data-testid="qbo-sub-cost-to-project" checked={cfg.costo_sub_a_proyecto !== false}
              onCheckedChange={(v) => saveRule.mutate({ costo_sub_a_proyecto: v })} />
          </div>
          {cfg.costo_sub_a_proyecto !== false && (
            <div>
              <p className="mb-1.5 text-[12px] text-ink-tertiary">{t("settings.accounting.cost_account")}</p>
              <Input data-testid="qbo-cost-account" defaultValue={cfg.cuenta_costo_sub || ""}
                key={cfg.cuenta_costo_sub || "vacia"}
                placeholder={t("settings.accounting.cost_account_ph")}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (cfg.cuenta_costo_sub || "")) saveRule.mutate({ cuenta_costo_sub: v });
                }}
                className="h-9 rounded-control text-[13px]" />
            </div>
          )}
        </div>

        {cfg.regla === "por_compania" && (
          <p className="mt-3 text-[12.5px] text-ink-tertiary">{t("settings.accounting.per_company_hint")}</p>
        )}
        {cfg.regla === "manual" && (
          <p className="mt-3 text-[12.5px] text-ink-tertiary">{t("settings.accounting.manual_hint")}</p>
        )}
      </div>
    </div>
  );
};
